import { sageAppUrl } from '@/env'
import { ISteamTrap } from '@/types'
import moment from 'moment-timezone'
import { v4 as uuidV4 } from 'uuid'
import { nextTick } from 'vue'
import { DATETIME_FORMAT, DEFAULT_TIMEZONE } from './Constants'
import { ParseDecimalNumber } from './ParseDecimalNumber'

export function trimValue(value: string, length: number) {
  const parsedValue = parseFloat(value)
  if (isNaN(parsedValue)) {
    return value
  }

  return parsedValue.toFixed(length).toString()
}

export function roundToMaxTwoDecimals(value: number) {
  if (value === 0) {
    return 0
  }
  if (!value) {
    return null
  }

  try {
    return Math.round(value * 100) / 100
  } catch (e) {
    return null
  }
}

export function convertMilligravityToGravity(mg: number) {
  // Divide by 1000 to convert milligravity to gravity
  // And, round the result to a max of two decimals
  return mg ? roundToMaxTwoDecimals(mg / 1000) : null
}

/**
 * Formats a number (or string that can be cast as a number) to a currency string.
 * @param value The value to be displayed as a currency string
 */
export function currencyFormat(value: number | string): string {
  const floatValue = ParseDecimalNumber(value)
  // In the future, we could have the user's language/region identifier and currency configured in a data store and create a custom formatter for them instead of hardcoding en-US and USD.
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  if (isNaN(floatValue)) {
    return ''
  }

  return formatter.format(floatValue)
}

export function macAddressInputEnforcer(previous: string, current: string): string {
  // force lower case
  current = current.toLowerCase()
  const temp = current
  let updatedValue = ''

  // This is the maximum length of a MAC Address with separators
  if (temp.length >= 23) {
    const charCode = temp.charCodeAt(temp.length - 1)
    if (
      !(charCode >= '0'.charCodeAt(0) && charCode <= ':'.charCodeAt(0)) &&
      !(charCode >= 'a'.charCodeAt(0) && charCode <= 'f'.charCodeAt(0))
    ) {
      // invalid character
      updatedValue = temp.substr(0, temp.length - 1)
    } else {
      updatedValue = temp.substring(0, 23)
    }
  } else {
    if (temp.length === 17) {
      if (previous[previous.length - 1] !== ':') {
        updatedValue = current
      } else {
        updatedValue = current.substring(0, current.length - 1)
      }
    } else if (temp.length === 18 && temp[temp.length - 1] !== ':') {
      let part = temp.substring(0, temp.length - 1)
      part += ':'
      part += temp[temp.length - 1]

      updatedValue = part
    } else {
      if (temp + ':' === previous) {
        // this means we just removed a colon which means we really wanted to remove the previous value
        updatedValue = temp.substr(0, temp.length - 1)
      } else {
        const charCode = temp.charCodeAt(temp.length - 1)
        if (
          !(charCode >= '0'.charCodeAt(0) && charCode <= ':'.charCodeAt(0)) &&
          !(charCode >= 'a'.charCodeAt(0) && charCode <= 'f'.charCodeAt(0))
        ) {
          // invalid character
          updatedValue = temp.substr(0, temp.length - 1)
        } else {
          const quotient = Math.floor(temp.length / 3)
          const leftover = temp.length - quotient * 3

          if (leftover === 2) {
            updatedValue = current + ':'
          } else {
            // it's just a valid character all by itself
            updatedValue = current
          }
        }
      }
    }
  }

  return updatedValue
}

export function getStateOfChargeForVCAP(vcap: number, vMax = 4.1, vMin = 1.8) {
  if ((vcap !== 0 && !vcap) || isNaN(vcap)) {
    return null
  }

  if (!isNaN(vcap)) {
    if (vcap > vMax) {
      vcap = vMax
    }
    if (vcap < vMin) {
      vcap = vMin
    }

    const stateOfCharge = 100 * ((vcap * vcap - vMin * vMin) / (vMax * vMax - vMin * vMin))
    let tens = Math.floor(Math.round(stateOfCharge) / 10)
    let tensRemainder = stateOfCharge - tens * 10

    if (tensRemainder <= 5) {
      if (tensRemainder < 2.5) {
        tensRemainder = 0
      } else {
        tensRemainder = 5
      }
    } else {
      if (tensRemainder < 7.5) {
        tensRemainder = 5
      } else {
        tensRemainder = 0
        tens++
      }
    }

    tens *= 10
    tens += tensRemainder

    return tens
  }
}

// retrieved from https://github.com/flatpickr/flatpickr/issues/930
export function parseFlatPickrDateFunction(timezone: string) {
  return (dateString, format) => {
    const timezonedDate = moment.tz(dateString, format, timezone)

    return new Date(
      timezonedDate.year(),
      timezonedDate.month(),
      timezonedDate.date(),
      timezonedDate.hour(),
      timezonedDate.minute(),
      timezonedDate.second()
    )
  }
}

// retrieved from https://github.com/flatpickr/flatpickr/issues/930
export function formatFlatPickrDateFunction(timezone: string) {
  return (date, format) => {
    return moment
      .tz(
        [
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes(),
          date.getSeconds()
        ],
        timezone
      )
      .locale('en')
      .format(format)
  }
}

export function baseFlatPickrConfig(timezone: string) {
  return {
    enableTime: true,
    altInput: true,
    altFormat: 'M/D/YYYY h:mm A',
    dateFormat: 'YYYY-MM-DD HH:mm',
    parseDate: parseFlatPickrDateFunction(timezone),
    formatDate: formatFlatPickrDateFunction(timezone),
    disableMobile: true
  }
}

// workaround for current flatpickr issue on mobile (https://github.com/flatpickr/flatpickr/issues/1508)
export function flatPickrReadonlyFix() {
  document.querySelectorAll('.flatpickr-input').forEach((flatpickr) => {
    flatpickr.nextElementSibling.setAttribute('readonly', 'readonly')
  })
}

export const domainRules = {
  regex: /^([A-Za-z0-9-]{1,63}\.)+[A-Za-z]{2,6}$/
}

export function idify(text: string) {
  return text.toLowerCase().replace(/\s+/g, '')
}

export const uuid = uuidV4

const macAddressRegex_64bit = /^([0-9A-Fa-f]{2}[:]){7}([0-9A-Fa-f]{2})$/
const macAddressRegex_48bit = /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/
export function isValidMacAddress(address: string): boolean {
  return Boolean(address.match(macAddressRegex_64bit) || address.match(macAddressRegex_48bit))
}

export function formatMHMDashboardBarChartValue(numberToFormat: number) {
  return numberToFormat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function formatMacAddress(address: string) {
  // If already valid and formatted, return
  if (isValidMacAddress(address)) {
    return address
  }

  // Place :s after every 2 characters and remove the trailing :
  const formattedAddress = address.replace(/(.{2})/g, '$1:').slice(0, -1)

  // Check if newly formatted address is valid, else return null
  return isValidMacAddress(formattedAddress) ? formattedAddress : null
}

// you can't use "Enum" as a type
type EnumType = { [s: number]: string }
export function getEnumValues(enumeration: EnumType) {
  return Object.keys(enumeration).map((key) => enumeration[key])
}

export function getSageSteamTrapLink(assetId: string): string {
  // If there isn't an assetId, just direct them to the regular SAGE login
  if (!assetId) {
    return `${sageAppUrl}`
  }

  return `${sageAppUrl}/r/equipment-editor/steam_trap/${assetId}`
}

export function getSageAssetId(steamTrap: ISteamTrap) {
  return steamTrap?.integrationInfo?.[0]?.assetId
}

export interface TimeUtilityOptions {
  timezone?: string
  format?: string
}

/**
 * Transform a unix timestamp into a formatted Date/Time String
 * in the specified timezone.
 *
 * Timezone may be specified in function call or when importing the composable functions.
 *
 * @param {number} timestamp
 * @param {TimeUtilityOptions} {timezone, format}
 * @return {string} Formatted Date/Time String
 */
export function unixTimestampToTimezoneString(timestamp: number, options: TimeUtilityOptions = {}) {
  const { timezone = DEFAULT_TIMEZONE, format = DATETIME_FORMAT } = options
  return moment.unix(timestamp).tz(timezone).format(format)
}

/**
 * Transform a Date/Time string in a particular Timezone into a unix timestamp
 *
 * Timezone may be specified in function call or when importing the composable functions.
 *
 * @param {string} dateString
 * @param {TimeUtilityOptions} {timezone, format}
 * @return {number} unix timestamp
 */
export function dateTimezoneStringToUnixTimestamp(
  dateString: string,
  options: TimeUtilityOptions = {}
) {
  const { timezone = DEFAULT_TIMEZONE, format = DATETIME_FORMAT } = options
  return moment.tz(dateString, format, true, timezone).unix()
}

const focusableElementQuerySelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'

function isDisplayed(element: HTMLElement): boolean {
  return element.style.display !== 'none'
}

/**
 * Returns back an array of focusable elements
 * that exist within the passed parent HTMLElement, or
 * an empty array if no parent passed.
 *
 * [Original Source](https://piccalil.li/quick-tip/load-all-focusable-elements-with-javascript/)
 *
 * @param {HTMLElement} parent HTML element
 * @returns {(HTMLElement[])} The focusable elements that we can find
 */
export function getAllFocusableElements(parent: HTMLElement) {
  if (!parent) {
    console.warn('You need to pass a parent HTMLElement')
    return [] // Return array so length queries will work
  }

  const elements = Array.from(parent.querySelectorAll<HTMLElement>(focusableElementQuerySelector))

  // filter out the elements that are set to `display: none` because focusing on them causes focus-traps to break
  return elements.filter((e) => e.style.display !== 'none')
}

export function getFirstFocusableElement<ElementType extends HTMLElement = HTMLElement>(
  parent: HTMLElement
): ElementType | null {
  if (!parent) {
    console.warn('You need to pass a parent HTMLElement')
    return null
  }

  const foundElement = parent.querySelector<ElementType>(focusableElementQuerySelector)

  return foundElement && isDisplayed(foundElement) ? foundElement : null
}

export function focusNextTick(element?: HTMLElement): void {
  nextTick(() => element?.focus())
}

export function padNumber(value: number): string {
  return value < 10 ? `0${value}` : `${value}`
}
